import i18n, { changeLocale } from "@plugins/i18n";
import DateTimeFormatter from "@utils/datetime.formatter";
import { isEmpty } from "@/utils/var.check";
import { ROUTE_STORE } from "@plugins/router/router.const";
import { isDefined, isNull } from "../../../utils/var.check";
import whitelistWebsiteApi from "@apis/whitelist.website.api";
import { appLocalStorage } from "@utils/app.storage";
import { APPEARANCE_SETTINGS_VIEW } from "@modules/app/service/app.constant";
import richtextEditorApi from "@apis/richtext.editor.api";
import ConfigurationApi from "@apis/configuration.api.ts";

const app = {
  namespaced: true,
  state: () => ({
    locale: i18n.locale.value,
    showLeftPanel: false,
    showRightPanel: false,
    showPageDrawer: false,
    serverTime: null,
    currentPage: {},
    networkConnected: navigator.onLine,
    pageDrawerExpanded: {},
    pageDrawerContentType: {}, // key: drawerName, value: drawer content type (e.g., "annotation", "conversation")
    pageDirty: {},
    processingStateById: {},
    contentSubpageById: {},
    errorById: {}, //key/id: errorCode(SLS-1234)
    integratorAccess: null, // list of accessible integrators eg. ["TTS", "SE", "LFA"]
    integratorAppList: {}, // list of integrators under categories like ALS, TTS, SE, LFA
    whitelistDomainMaps: {}, // list of whitelist domain url mapped with uuid
    whitelistDomainMapPromise: null,
    appearanceSettingsWidth: null,
    notebookAppearanceSettingsWidth: null,
    whitelistHtmlTags: null,
    whitelistHtmlTagsPromise: null,
    currentAcademicYear: null,
  }),
  getters: {
    locale: (state) => state.locale,
    showLeftPanel: (state) => state.showLeftPanel,
    showPageDrawer: (state) => state.showPageDrawer,
    currentRoute: (state, getters, rootState) => rootState[ROUTE_STORE] || {},
    currentRouteName: (state, getters) => getters.currentRoute && getters.currentRoute.name,
    getValueFromRouteParams: (state, getters) => (key) =>
      getters.currentRoute.params && getters.currentRoute.params[key],
    getValueFromRouteQuery: (state, getters) => (key) =>
      getters.currentRoute.query && getters.currentRoute.query[key],
    // TODO: baoqi to use current route for following getters
    currentPage: (state) => state.currentPage,
    currentPageTitle: (state) => {
      // these are key to locales messages
      const pageTitle = state.currentPage ? state.currentPage.pageTitle : "";
      return pageTitle ? pageTitle : "app_title";
    },
    serverTime: (state) => state.serverTime,
    serverTimeFormatted: (state) => {
      if (state.serverTime) {
        return DateTimeFormatter.formatToDateTime(state.serverTime);
      }
      return null;
    },
    checkNetworkConnected: (state) => {
      return state.networkConnected;
    },
    getPageDrawerExpanded: (state) => (drawerName) => {
      return state.pageDrawerExpanded[drawerName];
    },
    getPageDrawerContentType: (state) => (drawerName) => {
      return state.pageDrawerContentType[drawerName];
    },
    getPageDirty: (state) => (id) => {
      return state.pageDirty[id];
    },
    checkWholePageDirty: (state) => {
      return Object.keys(state.pageDirty).length > 0;
    },
    getProcessingState: (state) => (id) => {
      return state.processingStateById[id];
    },
    isContentSubPageOpened: (state) => {
      return !isEmpty(state.contentSubpageById);
    },
    getListOfError: (state) => {
      return Object.values(state.errorById).filter((error) => {
        return error.displayed !== true;
      });
    },
    isIntegratorAccessLoaded: (state) => {
      return state.integratorAccess !== null;
    },
    hasIntegratorAppCategoryAccess: (state) => (type) => {
      return !isEmpty(state.integratorAccess) && state.integratorAccess.includes(type);
    },
    getIntegratorAppAccessByCategory: (state) => (type) => {
      return Object.values(state.integratorAppList).filter((integratorType) => {
        return integratorType.category === type;
      });
    },
    getIntegratorAppAccessByCategoryList: (state) => (typeList) => {
      return Object.values(state.integratorAppList).filter((integratorType) => {
        return typeList.includes(integratorType.category);
      });
    },
    getIntegratorAppAccessByAppFeatureId: (state) => (appFeatureId) => {
      return isEmpty(state.integratorAppList[appFeatureId])
        ? null
        : state.integratorAppList[appFeatureId];
    },
    getIntegratorAppList: (state) => {
      return isEmpty(state.integratorAppList) ? null : state.integratorAppList;
    },
    isIntegratorAppEnabled: (state, getters) => (appType) => {
      const app = getters.getIntegratorAppAccessByCategory(appType);
      return !isEmpty(app.find((app) => app.accessible && app.enabled));
    },
    getWhitelistDomainMaps: (state) => () => {
      return state.whitelistDomainMaps;
    },
    getWhitelistDomainMapPromise: (state) => {
      return state.whitelistDomainMapPromise;
    },
    getWhitelistHtmlTags: (state) => {
      return state.whitelistHtmlTags;
    },
    getAppearanceSettingsWidth: (state) => {
      return state.appearanceSettingsWidth;
    },
    getNotebookAppearanceSettingsWidth: (state) => {
      return state.notebookAppearanceSettingsWidth;
    },
    getCurrentAcademicYear: (state) => {
      return state.currentAcademicYear;
    },
  },
  mutations: {
    toggleLeftPanel(state) {
      state.showLeftPanel = !state.showLeftPanel;
    },
    toggleRightPanel(state) {
      state.showRightPanel = !state.showRightPanel;
    },
    togglePageDrawer(state) {
      state.showPageDrawer = !state.showPageDrawer;
    },
    currentPage(state, currentPage) {
      state.currentPage = currentPage;
    },
    locale(state, newLocale) {
      state.currentPage = newLocale;
    },
    serverTime(state, serverTime) {
      state.serverTime = serverTime ? parseInt(serverTime) : serverTime;
    },
    networkConnected(state, connected) {
      state.networkConnected = connected;
    },
    addPageDrawerExpanded(state, { drawerName, isExpanded }) {
      state.pageDrawerExpanded[drawerName] = isExpanded;
    },
    addPageDrawerContentType(state, { drawerName, drawerContentType }) {
      state.pageDrawerContentType[drawerName] = drawerContentType;
    },
    removePageDrawerExpanded(state, { drawerName }) {
      delete state.pageDrawerExpanded[drawerName];
    },
    addPageDirty(state, { id }) {
      state.pageDirty[id] = true;
    },
    removePageDirty(state, { id }) {
      delete state.pageDirty[id];
    },
    clearPageDirty(state) {
      state.pageDirty = {};
    },
    setProcessingState(state, { id, triggerElement }) {
      state.processingStateById[id] = triggerElement;
    },
    removeProcessingState(state, id) {
      delete state.processingStateById[id];
    },
    addContentSubpage(state, id) {
      state.contentSubpageById[id] = true;
    },
    removeContentSubpage(state, id) {
      delete state.contentSubpageById[id];
    },
    addError(state, error) {
      state.errorById[error.id] = error;
    },
    removeError(state, id) {
      delete state.errorById[id];
    },
    setErrors(state, newErrorMap) {
      state.errorById = newErrorMap;
    },
    setErrorsDisplayed(state, errorIds) {
      errorIds.forEach((id) => {
        state.errorById[id].displayed = true;
      });
    },
    setIntegratorAccess(state, integratorList) {
      state.integratorAccess = integratorList;
    },
    setIntegratorAppList(state, integratorAppList) {
      state.integratorAppList = integratorAppList;
    },
    setWhitelistDomainMaps(state, domainMap) {
      state.whitelistDomainMaps = domainMap;
    },
    setWhitelistDomainMapPromise(state, whitelistDomainMapPromise) {
      state.whitelistDomainMapPromise = whitelistDomainMapPromise;
    },
    setWhitelistHtmlTags(state, whitelistHtmlTags) {
      state.whitelistHtmlTags = whitelistHtmlTags;
    },
    setWhitelistHtmlTagsPromise(state, whitelistHtmlTagsPromise) {
      state.whitelistHtmlTagsPromise = whitelistHtmlTagsPromise;
    },
    setAppearanceSettingsWidth(state, width) {
      state.appearanceSettingsWidth = width;
    },
    setNotebookAppearanceSettingsWidth(state, width) {
      state.notebookAppearanceSettingsWidth = width;
    },
    setCurrentAcademicYear(state, year) {
      state.currentAcademicYear = year;
    },
  },
  actions: {
    doToggleLeftPanel() {
      return new Promise((resolve) => {
        resolve();
      });
    },
    doToggleRightPanel() {
      return new Promise((resolve) => {
        resolve();
      });
    },
    setLocale(context, locale) {
      changeLocale(locale).then(() => {
        context.commit("locale", locale);
      });
    },
    setCurrentPage(context, currentPage) {
      context.commit("currentPage", currentPage);
    },
    setServerTime(context, timeMillis) {
      context.commit("serverTime", timeMillis);
    },
    setNetworkConnected(context, connected) {
      context.commit("networkConnected", connected);
    },
    addPageDrawerExpanded(context, { drawerName, isExpanded }) {
      context.commit("addPageDrawerExpanded", {
        drawerName: drawerName,
        isExpanded: isExpanded,
      });
    },
    addPageDrawerContentType(context, { drawerName, drawerContentType }) {
      context.commit("addPageDrawerContentType", {
        drawerName: drawerName,
        drawerContentType: drawerContentType,
      });
    },
    removePageDrawerExpanded(context, { drawerName }) {
      context.commit("removePageDrawerExpanded", {
        drawerName: drawerName,
      });
    },
    addPageDirty(context, id) {
      if (isNull(context.getters.getPageDirty(id))) {
        context.commit("addPageDirty", {
          id: id,
        });
      }
    },
    removePageDirty(context, id) {
      context.commit("removePageDirty", {
        id: id,
      });
    },
    clearPageDirty(context) {
      context.commit("clearPageDirty");
    },
    setProcessingState(context, { id, triggerElement }) {
      context.commit("setProcessingState", { id, triggerElement });
    },
    removeProcessingState(context, id) {
      context.commit("removeProcessingState", id);
    },
    addContentSubpage(context, id) {
      context.commit("addContentSubpage", id);
    },
    removeContentSubpage(context, id) {
      context.commit("removeContentSubpage", id);
    },
    addError(context, error) {
      if (!context.state.errorById[error.id]) {
        context.commit("addError", error);
      }
    },
    removeError(context, id) {
      context.commit("removeError", id);
    },
    setErrorsDisplayed(context, errorIds) {
      if (errorIds.length === 0) {
        return;
      }
      context.commit("setErrorsDisplayed", errorIds);
    },
    removeTimeoutError(context) {
      const errorMap = Object.assign({}, context.state.errorById);
      Object.keys(errorMap).forEach((id) => {
        const remove = isDefined(errorMap[id].hasTimeout) ? errorMap[id].hasTimeout : true;
        if (remove) {
          delete errorMap[id];
        }
      });
      context.commit("setErrors", errorMap);
    },
    setIntegratorAppList(context, integratorAppList) {
      const newIntegratorAppList = Object.assign({}, context.getters.getIntegratorAppList);
      integratorAppList.forEach((integratorApp) => {
        newIntegratorAppList[integratorApp.appFeatureId] = integratorApp;
      });
      context.commit("setIntegratorAppList", newIntegratorAppList);

      const integratorList = [];

      for (const integratorApp of Object.values(newIntegratorAppList)) {
        if (integratorApp.accessible === true && !integratorList.includes(integratorApp.category)) {
          integratorList.push(integratorApp.category);
        }
      }
      context.commit("setIntegratorAccess", integratorList);
    },
    initWhitelistDomainMapPromise(context) {
      if (context.state.whitelistDomainMapPromise === null) {
        const whitelistDomainMapPromise = whitelistWebsiteApi.fetchWhitelistDomainMaps();
        context.commit("setWhitelistDomainMapPromise", whitelistDomainMapPromise);
      }
    },
    fetchWhitelistHtmlTags(context) {
      return new Promise((resolve, reject) => {
        if (context.state.whitelistHtmlTags !== null) {
          resolve(context.state.whitelistHtmlTags);
          return;
        }

        let whitelistHtmlTagsPromise = context.state.whitelistHtmlTagsPromise;

        if (whitelistHtmlTagsPromise === null) {
          whitelistHtmlTagsPromise = richtextEditorApi.fetchWhiteList();
          context.commit("setWhitelistHtmlTagsPromise", whitelistHtmlTagsPromise);
        }

        whitelistHtmlTagsPromise.then(
          (response) => {
            if (response.code !== "ok") {
              reject(response.error);
              return;
            }

            context.commit("setWhitelistHtmlTags", response.data);
            resolve(response.data);
          },
          (error) => {
            reject(error);
          },
        );
      });
    },
    fetchWhitelistDomainMaps(context) {
      return new Promise((resolve, reject) => {
        const whitelistDomainMaps = context.getters.getWhitelistDomainMaps();

        if (!isEmpty(whitelistDomainMaps)) {
          resolve(whitelistDomainMaps);
        }
        whitelistWebsiteApi.fetchWhitelistDomainMaps().then(
          (response) => {
            if (response.code !== "ok") {
              reject(response.error);
            }
            context.commit("setWhitelistDomainMaps", response.data);
            resolve(response.data);
          },
          (error) => {
            reject(error);
          },
        );
      });
    },
    updateAppearanceSettingsWidth(context, width) {
      return new Promise((resolve) => {
        const userId = context.rootGetters["auth/userId"];
        const storageKey = `appearance-setting-width-${userId}`;
        appLocalStorage.setItem(storageKey, width);
        context.commit("setAppearanceSettingsWidth", width);
        resolve();
      });
    },
    loadAppearanceSettingsWidth(context) {
      return new Promise((resolve, reject) => {
        const userId = context.rootGetters["auth/userId"];
        const storageKey = `appearance-setting-width-${userId}`;
        let savedWidth = appLocalStorage.getItem(storageKey);

        if (!isEmpty(savedWidth)) {
          try {
            savedWidth = JSON.parse(savedWidth);
            if (Object.values(APPEARANCE_SETTINGS_VIEW).includes(savedWidth)) {
              context.commit("setAppearanceSettingsWidth", savedWidth);
              resolve(savedWidth);
            } else {
              const defaultWidth = APPEARANCE_SETTINGS_VIEW.STANDARD;
              context.commit("setAppearanceSettingsWidth", defaultWidth);
              resolve(defaultWidth);
            }
          } catch (error) {
            reject(error);
          }
        } else {
          const defaultWidth = APPEARANCE_SETTINGS_VIEW.STANDARD;
          context.commit("setAppearanceSettingsWidth", defaultWidth);
          resolve(defaultWidth);
        }
      });
    },
    updateNotebookAppearanceSettingsWidth(context, width) {
      return new Promise((resolve) => {
        const userId = context.rootGetters["auth/userId"];
        const storageKey = `notebook-appearance-setting-width-${userId}`;
        appLocalStorage.setItem(storageKey, width);
        context.commit("setNotebookAppearanceSettingsWidth", width);
        resolve();
      });
    },
    loadNotebookAppearanceSettingsWidth(context) {
      return new Promise((resolve, reject) => {
        const userId = context.rootGetters["auth/userId"];
        const storageKey = `notebook-appearance-setting-width-${userId}`;
        let savedWidth = appLocalStorage.getItem(storageKey);

        if (!isEmpty(savedWidth)) {
          try {
            savedWidth = JSON.parse(savedWidth);
            if (Object.values(APPEARANCE_SETTINGS_VIEW).includes(savedWidth)) {
              context.commit("setNotebookAppearanceSettingsWidth", savedWidth);
              resolve(savedWidth);
            } else {
              const defaultWidth = APPEARANCE_SETTINGS_VIEW.STANDARD;
              context.commit("setNotebookAppearanceSettingsWidth", defaultWidth);
              resolve(defaultWidth);
            }
          } catch (error) {
            reject(error);
          }
        } else {
          const defaultWidth = APPEARANCE_SETTINGS_VIEW.STANDARD;
          context.commit("setNotebookAppearanceSettingsWidth", defaultWidth);
          resolve(defaultWidth);
        }
      });
    },
    fetchCurrentAcademicYear(context) {
      if (context.state.currentAcademicYear !== null) {
        return Promise.resolve(context.state.currentAcademicYear);
      }

      return ConfigurationApi.fetchCurrentAcademicYear()
        .then(
          (response) => {
            if (response.code !== "ok") {
              return Promise.reject(new Error(`failed to fetch academic year: ${response.code}`));
            }

            context.commit("setCurrentAcademicYear", response.data);

            return response.data;
          },
          (error) => {
            return Promise.reject(error);
          },
        )
        .catch((error) => {
          console.error("Error fetching academic year:", error.message);

          return Promise.reject(new Error(`failed to fetch academic year: ${error}`));
        });
    },
  },
};

export default app;
